import { Component, DoCheck, EventEmitter, Input, Output, ViewChild, forwardRef, IterableDiffer, IterableDiffers } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonComponent } from 'components/input/button/button.component';
import { CustomInputComponent } from 'components/input/custom-input/custom-input.component';
import { ListColumnComponent, ListRowComponent, ListViewComponent, ListViewDirective } from 'components/layout/list-view/list-view.component';
import { CredentialType } from 'enums/CredentialType';
import { InputType } from 'enums/InputType';
import { SiteType } from 'enums/SiteType';
import { ValidationType } from 'enums/validationType';
import 'extensions/validation.ts';
import { PointerModel, PointerStatus } from 'models/generated/Distribution.Api.Models.Pointer';
import { SiteModel, SiteStatus } from 'models/generated/Distribution.Api.Models.Site';
import { ProductPlanModel } from 'models/generated/Entities.GormCrm.ProductPlan';
import { Layout } from 'modules/layout';
import { CustomDatePipe } from 'pipes/custom-date.pipe';
import { ProductNamePipe } from 'pipes/product-name.pipe';
import { TranslatePipe } from 'pipes/translate.pipe';
import { AiBuilderService } from 'services/ai-builder.service';
import { CompanyDataProvider, CompanyDataProviderFactory } from 'services/company-data-provider';
import { DnsPointerService } from 'services/dns-lookup.service';
import { FormValidationService } from 'services/form-validation.service';
import { InputContainerService } from 'services/input-container.service';
import { NotificationService } from 'services/notification.service';
import { SiteService } from 'services/site.service';
import { SelectWebsiteProductPlanComponent } from './add-company-site.component';
import { CompanySitesComponent } from './company-sites.component';

@Component({
	selector: 'website-details',
	templateUrl: './website-details.component.html',
	imports: [Layout, SelectWebsiteProductPlanComponent, ListViewComponent, ListRowComponent, ListColumnComponent, forwardRef(() => SiteAddPointerComponent), ListViewDirective, TranslatePipe, CustomDatePipe, ProductNamePipe]
})
export class WebsiteDetailsComponent implements DoCheck {

	private originalDiffer?: IterableDiffer<PointerModel>;

	private _site!: SiteModel;
	@Input()
	set site(value: SiteModel) {
		this._site = value;
		this.originalDiffer = this.iterableDiffers.find(this._site.pointers).create<PointerModel>();
		this.sortPointers();
	}
	get site(): SiteModel {
		return this._site;
	}

	@Input() productPlan!: ProductPlanModel;
	@ViewChild('aiBuilderButton') aiBuilderButton!: ButtonComponent;
	@ViewChild(SelectWebsiteProductPlanComponent) selectWebsiteProductPlan?: SelectWebsiteProductPlanComponent;

	CredentialType = CredentialType;
	siteStatusTypeEnum = SiteStatus;
	siteTypeEnum = SiteType;
	pointersStatusType = PointerStatus;
	showAddPointer = false;
	showChangeProductPlan = false;
	productPlanChangeBusy = false;
	pointerInProcessOfChangingToPrimary?: PointerModel;
	newProductPlanId?: string;
	sortedSitePointers: PointerModel[] = [];

	private dataProvider: CompanyDataProvider;

	constructor(public readonly siteService: SiteService,
		public readonly parent: CompanySitesComponent,
		public readonly dns: DnsPointerService,
		private readonly notificationService: NotificationService,
		private readonly aiBuilderService: AiBuilderService,
		dataProviderFactory: CompanyDataProviderFactory,
		private iterableDiffers: IterableDiffers) {
		this.dataProvider = dataProviderFactory.getProviderForCurrentScope();
	}

	ngDoCheck() {
		if (!this._site.pointers || !this.originalDiffer) return;
		const originalChanges = this.originalDiffer.diff(this._site.pointers);
		if (originalChanges) 
			this.sortPointers();
	}

	notLegacySite() {
		return this.site.type == SiteType.Dsa || this.site.type == SiteType.WebserverApi || this.site.type == SiteType.AiBuilder;
	}

	canModifyPointer(pointer: PointerModel): boolean {
		return this.editablePointer(pointer, [PointerStatus.Completed]);
	}

	canDeletePointer(pointer: PointerModel): boolean {
		return this.editablePointer(pointer, [PointerStatus.Completed, PointerStatus.Failed]);
	}

	private editablePointer(pointer: PointerModel, allowedStatusses: PointerStatus[]): boolean {
		if (pointer.primaryPointer) return false;
		if (pointer == this.pointerInProcessOfChangingToPrimary) return false;
		if (!allowedStatusses.includes(pointer.status)) return false;
		if (!this.notLegacySite()) return false;

		return true;
	}

	setSitePrimaryPointer(site: SiteModel, pointer: PointerModel) {
		this.pointerInProcessOfChangingToPrimary = pointer;
		this.siteService.setSitePrimaryPointer(site.id!, pointer.id!).then(() => {
			this.pointerInProcessOfChangingToPrimary = undefined;
		});
	}

	async changeProductPlan(newProductPlanId: string) {
		if (newProductPlanId == this.productPlan.id) return;
		this.productPlanChangeBusy = true;

		await this.siteService.changeProductPlan(this.site.id!, newProductPlanId);
		this.showChangeProductPlan = false;
		this.productPlanChangeBusy = false;
	}

	deletePointer(pointer: PointerModel) {
		this.notificationService.confirm('site.pointer.delete-confirmation', { 'pointer': pointer.name }).subscribe(result => {
			if (!result) return;
			pointer.status = PointerStatus.InProgressForDeletion;
			this.siteService.deletePointer(pointer.id!).then(errorMessage => {
				if (errorMessage) {
					pointer.status = PointerStatus.FailedToDelete;
					this.notificationService.error('site.dsa-error', errorMessage);
				}
			});
		});
	}

	addSsl() {
		this.parent.addSsl(this.site, this.dns.getValidPointers(this.site));
	}

	async launchAiBuilder() {
		await this.aiBuilderService.launchAiBuilder(this.site, this.dataProvider);
		this.aiBuilderButton.reset();
	}

	sortPointers() {
		if (!this.site) return;
		this.sortedSitePointers = [...this.site.pointers.sort((a, b) => {
			const primaryDiff = Number(b.primaryPointer) - Number(a.primaryPointer);
			if (primaryDiff !== 0) return primaryDiff;
			const aIsTest = this.siteService.isTestAddress(a.name);
			const bIsTest = this.siteService.isTestAddress(b.name);
			return Number(aIsTest) - Number(bIsTest);
		})];
	}
}

@Component({
	selector: 'site-add-pointer',
	templateUrl: './add-site-pointer.component.html',
	providers: [InputContainerService, FormValidationService],
	imports: [FormsModule, CustomInputComponent, ButtonComponent, TranslatePipe]
})
export class SiteAddPointerComponent {

	@ViewChild('addPointerButton') addPointerButton?: ButtonComponent;

	@Input() site!: SiteModel;
	@Output() onSubmitted = new EventEmitter();

	validationType = ValidationType;
	inputTypeEnum = InputType;
	pointerName = '';
	validationMessage = '';

	constructor(private readonly siteService: SiteService, private readonly notificationService: NotificationService, private inputContainer: InputContainerService) {
		setTimeout(() => {
			this.inputContainer.focus();
		});
	}

	validate(): boolean {
		this.validationMessage = '';
		if (this.site?.pointers.some(d => d.name === this.pointerName.toLowerCase())) { this.validationMessage = 'domain-already-in-use'; }
		if (!this.pointerName.toLowerCase().isValidDomain()) { this.validationMessage = 'domain'; }
		if (this.pointerName == '') { this.validationMessage = 'required'; }
		return this.validationMessage == '';
	}

	async addPointer(pointer: string, site: SiteModel) {
		const errorMessage = await this.siteService.addPointerToSite(pointer, site);

		if (errorMessage) {
			this.notificationService.error('site.dsa-error', errorMessage);
			this.addPointerButton?.reset();
		} else {
			this.pointerName = '';
			this.addPointerButton?.succeeded();
			this.onSubmitted.emit();
		}
	}
}
